import type { ErrorMap } from '@/app/error-registry';

export const FORMS_CREATE_ERROR_MAP: ErrorMap = {
  POST: {
    validation: 'forms:apiErrors.createValidation',
    default: 'forms:apiErrors.createDefault',
  },
};

export const FORMS_UPDATE_ERROR_MAP: ErrorMap = {
  PUT: {
    // Confirmed (2026-07-09 backend guide): kept its original name despite the
    // FormStatus `Published`→`Shared` rename — do not rename to `Form.AlreadyShared`.
    'Form.AlreadyPublished': 'forms:apiErrors.updateNotDraft',
    'Form.NotFound': 'forms:apiErrors.notFound',
    validation: 'forms:apiErrors.updateValidation',
    default: 'forms:apiErrors.updateDefault',
  },
};

export const FORMS_LIFECYCLE_ERROR_MAP: ErrorMap = {
  POST: {
    // Confirmed — kept its original name, see note above.
    'Form.CannotCloseNonPublished': 'forms:apiErrors.closeNotShared',
    'Form.AlreadyArchived': 'forms:apiErrors.alreadyArchived',
    'Form.NotFound': 'forms:apiErrors.notFound',
    default: 'forms:apiErrors.lifecycleDefault',
  },
};

/** Confirmed error codes for `POST /forms/{formId}/share` (2026-07-09 backend guide). */
export const FORMS_SHARING_ERROR_MAP: ErrorMap = {
  POST: {
    'Form.NotFound': 'forms:apiErrors.notFound',
    'Form.AlreadyArchived': 'forms:apiErrors.shareFormNotAvailable',
    'Form.InvalidAudienceType': 'forms:apiErrors.shareInvalidAudience',
    'Form.InvalidRecipientType': 'forms:apiErrors.shareInvalidRecipientType',
    'Form.InvalidExpiresAt': 'forms:apiErrors.shareInvalidExpiresAt',
    'Form.InvalidRecipientUserId': 'forms:apiErrors.shareInvalidRecipientUserId',
    'Form.RecipientAlreadyExists': 'forms:apiErrors.shareRecipientDuplicate',
    'Form.AudienceRequired': 'forms:apiErrors.shareAudienceRequired',
    'Form.NoRecipients': 'forms:apiErrors.shareNoRecipients',
    'FormRecipient.MemberRequiresUserId': 'forms:apiErrors.shareRecipientMissingUserId',
    'FormRecipient.EmailRequiresAddress': 'forms:apiErrors.shareRecipientMissingEmail',
    validation: 'forms:apiErrors.sharingValidation',
    default: 'forms:apiErrors.sharingDefault',
  },
};

/** Confirmed error codes for `DELETE /forms/{formId}` (2026-07-09 backend guide). */
export const FORMS_DELETE_ERROR_MAP: ErrorMap = {
  DELETE: {
    'Form.NotFound': 'forms:apiErrors.notFound',
    'Form.HasSubmissions': 'forms:apiErrors.deleteHasSubmissions',
    default: 'forms:apiErrors.deleteDefault',
  },
};

/** No specific error codes confirmed yet for `POST /forms/{formId}/duplicate` — default fallback only. */
export const FORMS_DUPLICATE_ERROR_MAP: ErrorMap = {
  POST: {
    'Form.NotFound': 'forms:apiErrors.notFound',
    validation: 'forms:apiErrors.duplicateValidation',
    default: 'forms:apiErrors.duplicateDefault',
  },
};

/** Extended per PR #90 (backend forms remediation, 2026-07-17 frontend notes). */
export const FORMS_SUBMIT_ERROR_MAP: ErrorMap = {
  POST: {
    'Form.NotAcceptingSubmissions': 'forms:apiErrors.submitClosed',
    'Form.CannotSubmitOnClosed': 'forms:apiErrors.submitClosed',
    'Form.CannotSubmitOnArchived': 'forms:apiErrors.submitArchived',
    'Form.NotPublished': 'forms:apiErrors.submitNotPublished',
    'Form.Expired': 'forms:apiErrors.submitExpired',
    'Form.FieldNotFound': 'forms:apiErrors.submitFieldNotFound',
    'Form.RequiredFieldMissing': 'forms:apiErrors.submitValidation',
    'Form.DuplicateFieldValue': 'forms:apiErrors.submitValidation',
    'Form.EmailRequired': 'forms:apiErrors.submitEmailRequired',
    'Form.InvalidFieldData': 'forms:apiErrors.submitInvalidFieldData',
    'Form.InvalidShareToken': 'forms:apiErrors.submitInvalidShareToken',
    'Form.NotRecipient': 'forms:apiErrors.submitNotRecipient',
    validation: 'forms:apiErrors.submitValidation',
    default: 'forms:apiErrors.submitDefault',
  },
};

/**
 * `GET /forms/{formId}/public` and `GET /forms/access/{token}` — page-load reads.
 * Added per PR #90: a `Shared`-but-expired form now 409s here instead of being returned.
 */
export const FORMS_PUBLIC_READ_ERROR_MAP: ErrorMap = {
  GET: {
    'Form.Expired': 'forms:apiErrors.submitExpired',
    'Form.CannotSubmitOnClosed': 'forms:apiErrors.submitClosed',
    'Form.CannotSubmitOnArchived': 'forms:apiErrors.submitArchived',
    'Form.NotPublished': 'forms:apiErrors.submitNotPublished',
    'Form.NotFound': 'forms:apiErrors.notFound',
    default: 'forms:apiErrors.publicLoadDefault',
  },
};

/** No specific error codes confirmed yet for `DELETE /forms/{formId}/submissions/{submissionId}` — default fallback only. */
export const FORMS_DELETE_SUBMISSION_ERROR_MAP: ErrorMap = {
  DELETE: {
    'Submission.NotFound': 'forms:apiErrors.notFound',
    default: 'forms:apiErrors.deleteSubmissionDefault',
  },
};

export const FORMS_CREATE_TASK_ERROR_MAP: ErrorMap = {
  POST: {
    'Submission.TaskAlreadyCreated': 'forms:apiErrors.taskAlreadyCreated',
    'Submission.AlreadyPending': 'forms:apiErrors.taskPending',
    'Submission.NotFound': 'forms:apiErrors.notFound',
    // PR #90 frontend notes name this as `Form.SubmissionNotFound` — kept both since the
    // backend guide and the actual code weren't confirmed to match; harmless if only one fires.
    'Form.SubmissionNotFound': 'forms:apiErrors.notFound',
    default: 'forms:apiErrors.taskDefault',
  },
};

/** Confirmed error codes for `POST /forms/{formId}/save-as-template` (2026-07-09 backend guide). */
export const FORMS_SAVE_AS_TEMPLATE_ERROR_MAP: ErrorMap = {
  POST: {
    'Form.NotFound': 'forms:apiErrors.notFound',
    'FormTitle.Required': 'forms:apiErrors.templateTitleRequired',
    'FormTitle.TooLong': 'forms:apiErrors.templateTitleTooLong',
    default: 'forms:apiErrors.saveAsTemplateDefault',
  },
};

/** Confirmed error codes for `POST /templates` (2026-07-09 backend guide). */
export const TEMPLATES_CREATE_ERROR_MAP: ErrorMap = {
  POST: {
    'FormTitle.Required': 'forms:apiErrors.templateTitleRequired',
    'FormTitle.TooLong': 'forms:apiErrors.templateTitleTooLong',
    'FormTemplate.EmptyFields': 'forms:apiErrors.templateEmptyFields',
    validation: 'forms:apiErrors.templateCreateValidation',
    default: 'forms:apiErrors.templateCreateDefault',
  },
};

/** Confirmed error codes for `PUT /templates/{templateId}` (2026-07-09 backend guide). */
export const TEMPLATES_UPDATE_ERROR_MAP: ErrorMap = {
  PUT: {
    'FormTemplate.NotFound': 'forms:apiErrors.templateNotFound',
    'FormTitle.Required': 'forms:apiErrors.templateTitleRequired',
    'FormTitle.TooLong': 'forms:apiErrors.templateTitleTooLong',
    'FormTemplate.EmptyFields': 'forms:apiErrors.templateEmptyFields',
    validation: 'forms:apiErrors.templateUpdateValidation',
    default: 'forms:apiErrors.templateUpdateDefault',
  },
};

/** Confirmed error code for `DELETE /templates/{templateId}` (2026-07-09 backend guide). */
export const TEMPLATES_DELETE_ERROR_MAP: ErrorMap = {
  DELETE: {
    'FormTemplate.NotFound': 'forms:apiErrors.templateNotFound',
    default: 'forms:apiErrors.templateDeleteDefault',
  },
};

/** No specific error codes confirmed yet for `POST /templates/{templateId}/duplicate` — default fallback only. */
export const TEMPLATES_DUPLICATE_ERROR_MAP: ErrorMap = {
  POST: {
    'FormTemplate.NotFound': 'forms:apiErrors.templateNotFound',
    default: 'forms:apiErrors.templateDuplicateDefault',
  },
};

/** No specific error codes confirmed yet for `POST /templates/{templateId}/instantiate` — default fallback only. */
export const TEMPLATES_INSTANTIATE_ERROR_MAP: ErrorMap = {
  POST: {
    'FormTemplate.NotFound': 'forms:apiErrors.templateNotFound',
    default: 'forms:apiErrors.templateInstantiateDefault',
  },
};
